import { BookUser, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ConfirmDelete } from '@/components/ConfirmDelete';
import { toast } from '@/hooks/use-toast';
import { useDeleteContact } from '@/hooksApi/useContact';
import { Contact } from '@/types/contacts';

interface ContactRowProps {
  contact: Contact;
  onEdit: (uid: string) => void;
}

export const ContactRow = ({ contact, onEdit }: ContactRowProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutate: deleteContact, isLoading: isDeleting } = useDeleteContact();

  const handleDelete = () => {
    if (isDeleting) return;
    deleteContact(contact.uid, {
      onSuccess: () => {
        queryClient.invalidateQueries(['contacts']);
        toast({ title: String(t('contact_deleted')) });
      },
    });
  };

  return (
    <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm transition-shadow hover:shadow-md'>
      <div className='p-5 flex items-start gap-4'>
        <div className='h-10 w-10 rounded-lg bg-sd-muted flex items-center justify-center shrink-0 mt-0.5'>
          <BookUser className='h-5 w-5 text-sd-muted-foreground' />
        </div>
        <div className='flex-1 min-w-0'>
          <div className='flex items-center gap-2 mb-1'>
            <h3 className='text-sm font-semibold text-sd-foreground m-0'>{contact.full_name}</h3>
          </div>
          <p className='text-xs text-sd-muted-foreground m-0'>
            {contact.region?.area_name && `${contact.region.area_name}, `}
            {contact.region?.province && `${contact.region.province} · `}
            {contact.address_text}
          </p>
          <div className='flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5'>
            <p className='text-sm font-medium text-sd-foreground m-0' dir='ltr'>
              {contact.phone}
            </p>
            {contact.additional_phones?.[0] && (
              <p className='text-xs text-sd-muted-foreground m-0' dir='ltr'>
                {contact.additional_phones[0]}
              </p>
            )}
          </div>
        </div>
        <div className='flex items-center gap-2 shrink-0'>
          <button
            onClick={() => onEdit(contact.uid)}
            aria-label={String(t('edit_contact'))}
            className='h-8 w-8 rounded-lg border border-sd-input bg-sd-background flex items-center justify-center hover:bg-sd-muted transition-colors'
          >
            <Pencil className='h-3.5 w-3.5 text-sd-muted-foreground' />
          </button>
          <ConfirmDelete
            title={String(t('delete_contact'))}
            description={String(t('delete_contact_description'))}
            onConfirm={handleDelete}
            disabled={isDeleting}
          />
        </div>
      </div>
    </div>
  );
};
